import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Laptop, Loader2, RefreshCw, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { mcpCall } from '../lib/mcp';
import { type LiveTenantOption, LiveTenantPicker } from './LiveTenantPicker';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';
import { Input } from './ui/Input';

type AdoptionMachine = {
  hardware_id: string;
  hostname?: string | null;
  ip?: string | null;
  mac?: string | null;
  tenant_slug?: string | null;
  ownership?: string | null;
  install_state?: string | null;
  running?: boolean;
  last_seen?: string | null;
};

type AdoptionSession = {
  id: string;
  pair_code: string;
  pair_label?: string;
  status: string;
  hardware_id?: string | null;
  hostname?: string | null;
  ip?: string | null;
  mac?: string | null;
  tenant_slug?: string | null;
  ownership?: string | null;
  intent_label?: string | null;
};

type AdoptionOverview = {
  ok: boolean;
  sessions: AdoptionSession[];
  machines: AdoptionMachine[];
  tenants: LiveTenantOption[];
  waiting: number;
};

type AssignResult = {
  ok: boolean;
  error?: string;
  hardware_id?: string;
  ownership?: string;
  tenant_slug?: string;
  ca?: string;
  action?: string;
  can_install?: boolean;
  message?: string;
};

function machineTitle(machine: AdoptionMachine): string {
  return machine.hostname || machine.ip || machine.mac || machine.hardware_id;
}

export function NodeAdoptionPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [hardwareId, setHardwareId] = useState('');
  const [tenantSlug, setTenantSlug] = useState('');
  const [label, setLabel] = useState('');
  const [orgName, setOrgName] = useState('');
  const [rootDomain, setRootDomain] = useState('');
  const [result, setResult] = useState<AssignResult | null>(null);

  const overview = useQuery({
    queryKey: ['deploy-node-adoption'],
    queryFn: () => mcpCall<AdoptionOverview>('deploy.live_kiosk_sessions', {}),
    refetchInterval: 5_000,
  });

  const machines = useMemo(() => {
    const byId = new Map<string, AdoptionMachine>();
    for (const machine of overview.data?.machines ?? []) {
      byId.set(machine.hardware_id, machine);
    }
    for (const session of overview.data?.sessions ?? []) {
      if (!session.hardware_id) continue;
      const current = byId.get(session.hardware_id);
      byId.set(session.hardware_id, {
        hardware_id: session.hardware_id,
        hostname: session.hostname ?? current?.hostname,
        ip: session.ip ?? current?.ip,
        mac: session.mac ?? current?.mac,
        tenant_slug: session.tenant_slug ?? current?.tenant_slug,
        ownership: session.ownership ?? current?.ownership,
        install_state: current?.install_state,
        running: current?.running,
        last_seen: current?.last_seen,
      });
    }
    return [...byId.values()];
  }, [overview.data]);

  useEffect(() => {
    if (hardwareId || machines.length !== 1) return;
    setHardwareId(machines[0]?.hardware_id ?? '');
  }, [hardwareId, machines]);

  useEffect(() => {
    if (tenantSlug || !overview.data?.tenants?.length) return;
    const preferred = overview.data.tenants.find((tenant) => tenant.local_owner) ??
      overview.data.tenants[0];
    setTenantSlug(preferred?.slug ?? '');
  }, [overview.data, tenantSlug]);

  const assign = useMutation({
    mutationFn: () =>
      mcpCall<AssignResult>('deploy.live_machine_assign', {
        hardware_id: hardwareId,
        tenant_slug: tenantSlug,
        label: label.trim() || undefined,
        new_org: tenantSlug === '__new__',
        org_name: tenantSlug === '__new__' ? orgName.trim() : undefined,
        root_domain: tenantSlug === '__new__' ? rootDomain.trim() : undefined,
      }),
    onSuccess: (next) => {
      setResult(next);
      if (!next.ok) {
        toast.error(
          next.error ?? t('nodeAdoption.assignFailed', 'Přiřazení selhalo'),
        );
        return;
      }
      toast.success(
        next.message ?? t('nodeAdoption.assigned', 'Vlastník nodu přiřazen'),
      );
      void qc.invalidateQueries({ queryKey: ['deploy-node-adoption'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const selected = machines.find((machine) => machine.hardware_id === hardwareId) ?? null;

  return (
    <Card>
      <CardHeader className='flex flex-row items-center justify-between gap-2'>
        <CardTitle className='flex items-center gap-2 text-base'>
          <ShieldCheck size={17} />
          {t('nodeAdoption.title', 'Adopce nového nodu')}
        </CardTitle>
        <div className='flex items-center gap-2'>
          {(overview.data?.waiting ?? 0) > 0 && (
            <Badge variant='yellow'>
              {t('nodeAdoption.waiting', '{{count}} čeká', {
                count: overview.data?.waiting,
              })}
            </Badge>
          )}
          <Button
            size='sm'
            variant='secondary'
            icon={overview.isFetching
              ? <Loader2 size={13} className='animate-spin' />
              : <RefreshCw size={13} />}
            onClick={() => overview.refetch()}
          >
            {t('common.refresh', 'Obnovit')}
          </Button>
        </div>
      </CardHeader>
      <CardContent className='space-y-4'>
        <p className='text-xs text-gray-500'>
          {t(
            'nodeAdoption.description',
            'Nejdřív přiřaď vlastníka infrastruktury. Mesh členství vznikne až ověřeným Sub-CA/spoke enrollem; samotné pozorování nod do meshe nepřidává.',
          )}
        </p>

        {overview.isLoading && (
          <div className='flex items-center gap-2 text-sm text-gray-500'>
            <Loader2 size={14} className='animate-spin' />
            {t('nodeAdoption.loading', 'Načítám objevené stroje…')}
          </div>
        )}

        {!overview.isLoading && machines.length === 0 && (
          <div className='rounded-lg border border-dashed border-gray-800 p-3 text-sm text-gray-500'>
            {t(
              'nodeAdoption.empty',
              'Žádný nový stroj se zatím neohlásil přes Deploy beacon.',
            )}
          </div>
        )}

        {machines.length > 0 && (
          <div className='grid gap-2 md:grid-cols-2'>
            {machines.map((machine) => {
              const active = machine.hardware_id === hardwareId;
              return (
                <button
                  key={machine.hardware_id}
                  type='button'
                  onClick={() => {
                    setHardwareId(machine.hardware_id);
                    setTenantSlug(machine.tenant_slug ?? tenantSlug);
                    setLabel(machine.hostname ?? '');
                    setResult(null);
                  }}
                  className={`rounded-lg border p-3 text-left transition-colors ${
                    active
                      ? 'border-blue-500/50 bg-blue-950/30'
                      : 'border-gray-800 bg-gray-950/30 hover:border-gray-600'
                  }`}
                >
                  <div className='flex items-center gap-2'>
                    <Laptop
                      size={15}
                      className={active ? 'text-blue-300' : 'text-gray-500'}
                    />
                    <span className='font-medium text-sm text-white'>
                      {machineTitle(machine)}
                    </span>
                    {machine.running && <Badge variant='green'>RUNNING</Badge>}
                  </div>
                  <div className='mt-1 space-y-0.5 font-mono text-[10px] text-gray-500'>
                    <div>{machine.hardware_id}</div>
                    <div>
                      {[machine.ip, machine.mac].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <div className='mt-2 flex flex-wrap gap-1'>
                    {machine.ownership && <Badge>{machine.ownership}</Badge>}
                    {machine.tenant_slug && <Badge>{machine.tenant_slug}</Badge>}
                    {machine.install_state && <Badge>{machine.install_state}</Badge>}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {selected && (
          <div className='space-y-3 border-t border-gray-800 pt-3'>
            <LiveTenantPicker
              tenants={overview.data?.tenants ?? []}
              value={tenantSlug}
              onChange={(slug) => {
                setTenantSlug(slug);
                setResult(null);
              }}
            />
            <Input
              size='sm'
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder={t('nodeAdoption.label', 'Popisek / cílový hostname')}
            />
            {tenantSlug === '__new__' && (
              <div className='grid gap-2 md:grid-cols-2'>
                <Input
                  size='sm'
                  value={orgName}
                  onChange={(event) => setOrgName(event.target.value)}
                  placeholder={t(
                    'nodeAdoption.orgName',
                    'Jméno nové organizace',
                  )}
                />
                <Input
                  size='sm'
                  value={rootDomain}
                  onChange={(event) => setRootDomain(event.target.value)}
                  placeholder={t(
                    'nodeAdoption.rootDomain',
                    'Root doména, např. firma.cz',
                  )}
                />
              </div>
            )}
            <div className='flex flex-wrap items-center gap-2'>
              <Button
                size='sm'
                onClick={() => assign.mutate()}
                disabled={!hardwareId || !tenantSlug || assign.isPending}
              >
                {assign.isPending
                  ? <Loader2 size={14} className='animate-spin' />
                  : t('nodeAdoption.assign', 'Přiřadit vlastníka')}
              </Button>
              {result?.ok && (
                <div className='flex flex-wrap gap-1'>
                  {result.ownership && <Badge>{result.ownership}</Badge>}
                  {result.ca && <Badge>{result.ca}</Badge>}
                  {result.action && <Badge>{result.action}</Badge>}
                  <Badge variant={result.can_install ? 'green' : 'yellow'}>
                    {result.can_install ? 'READY' : 'WAITING FOR TRUST'}
                  </Badge>
                </div>
              )}
            </div>
            {result?.message && <p className='text-xs text-gray-400'>{result.message}</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
